import {
    ConfirmProps,
    showConfirmMessage,
    showErrorMessage,
    showInfoMessage
} from '../../../_shared/Messages/actions/MessageActions';
import {APIActionStatus, Form, formsStore} from '../../../stores/Form';
import messagesText, {moreInfoText} from '../../../_shared/Messages/texts';
import {messagesIds} from '../../../_shared/Messages/consts';
import {htmlFormMessagesText} from '../components/HTMLFormEditor/texts';
import {priority} from '@priority-software/priority-proto-api/lib';
import {endForm, unlockHtmlForm} from '../../../services/form/form';
import {getUsername} from '../../../_shared/GWTBridge/invoke/genPage';
import {handleMessage, showError} from '../../Messages/actions';

export const defaultConfirmProps = () => {
    return {
        confirmButtonText: messagesText.yes,
        cancleButtonText: messagesText.cancel,
        defaultButton: 0
    };
};

export const undoApproveProps = () => {
    return {
        id: messagesIds.screens.ctrlZConfirm,
        content: messagesText.ctrlZContent,
        moreInfo: moreInfoText?.ctrlZContent,
        isRemovable: true,
        confirmButtonText: messagesText.yesDeleteChanges,
        cancleButtonText: messagesText.dontDelete,
        defaultButton: 0
    };
};

export const showFormClientSideMessage = (
    session: string,
    msgType: priority.netitems.shared.Message.Type,
    msgParams: Partial<
        ConfirmProps & {
            returnActiveLevelOnCancel?: boolean; // Restore the default active level when the cancel callback is called. The default value is: `true`
        }
    > = {}
) => {
    const screen = formsStore.getScreenBySession(session);
    const curLevel = screen?.activeLevel;
    const {returnActiveLevelOnCancel = true} = msgParams;

    const params = {
        ...defaultConfirmProps(),
        ...msgParams,
        onConfirm: async () => {
            screen?.setActiveLevel(curLevel);
            await msgParams.onConfirm?.();
        },
        onCancel: async () => {
            if (returnActiveLevelOnCancel) {
                await screen?.setActiveLevel(curLevel);
            }
            await msgParams.onCancel?.();
        },
        onClose: async () => {
            await screen?.setActiveLevel(curLevel);
            await msgParams.onClose?.();
        }
    };

    screen?.setActiveLevel(null);

    if (msgType === priority.netitems.shared.Message.Type.Information) {
        showInfoMessage(params);
    } else if (msgType === priority.netitems.shared.Message.Type.Warning) {
        showConfirmMessage(params);
    } else if (msgType === priority.netitems.shared.Message.Type.Error) {
        showErrorMessage(params);
    }
};

export const handleHTMLFormOpenMessages = async (
    startRs: priority.netitems.form.IStartRs | null,
    parentForm: Form | null
) => {
    if (startRs?.Messages?.length > 0) {
        const isError =
            startRs.Messages?.[0]?.MType === priority.netitems.shared.Message.Type.Error;

        if (isError && parentForm) {
            return await handleStartSubFormError(startRs, parentForm);
        }
        if (startRs.Messages?.[0]?.MType === priority.netitems.shared.Message.Type.Fatal) {
            return handleMessage(startRs, parentForm.session);
        }
    }
    return APIActionStatus.passed;
};

export const handleHTMLLockedState = async (
    subForm: Form,
    prefetchData: boolean
): Promise<APIActionStatus> => {
    return new Promise(async resolve => {
        if (prefetchData) {
            return resolve(APIActionStatus.passed);
        }

        if (!subForm.lockBy) {
            return resolve(APIActionStatus.passed);
        } else {
            const lockedByOtherUser = subForm.lockBy !== getUsername();

            const onClose = async () => {
                await endForm(subForm.session);
                resolve(APIActionStatus.blocked);
            };

            const onCancel = async () => {
                resolve(APIActionStatus.passed);
            };

            if (lockedByOtherUser) {
                showFormClientSideMessage(
                    subForm.session,
                    priority.netitems.shared.Message.Type.Information,
                    {
                        title: htmlFormMessagesText.lockedByAnotherUser.replace(
                            '{user}',
                            subForm.lockBy
                        ),
                        confirmButtonText: htmlFormMessagesText.approveLabel,
                        defaultButton: 0,
                        onClose,
                        onConfirm: onClose
                    }
                );
            } else {
                showFormClientSideMessage(
                    subForm.session,
                    priority.netitems.shared.Message.Type.Warning,
                    {
                        confirmButtonText: htmlFormMessagesText.editLabel,
                        cancleButtonText: htmlFormMessagesText.readOnlyLabel,
                        title: htmlFormMessagesText.lockedByUser,
                        defaultButton: 1,
                        onConfirm: async () => {
                            const res = await unlockHtmlForm(subForm.session);

                            if (res.Messages?.length > 0) {
                                resolve(APIActionStatus.blocked);
                            } else {
                                // remove lockBy property when unlock HTML form
                                subForm.resetLockBy();
                                resolve(APIActionStatus.passed);
                            }
                        },
                        onCancel,
                        returnActiveLevelOnCancel: false, // for this case the active level should be managed within the caller function
                        onClose
                    }
                );
            }
        }
    });
};

export const handleStartSubFormError = async (
    startRs: priority.netitems.form.IStartRs | null,
    parentForm: Form | null
) => {
    if (!startRs) {
        return APIActionStatus.blocked;
    }
    if (startRs?.Messages?.length > 0) {
        const isError =
            startRs.Messages?.[0]?.MType === priority.netitems.shared.Message.Type.Error;

        if (isError && parentForm) {
            const moveToParent = async () => {
                const activeScreen = formsStore.getScreenBySession(parentForm.session);

                if (parentForm.level !== activeScreen.activeLevel) {
                    await activeScreen.setActiveLevel(parentForm.level);
                }
            };

            return showError(parentForm.session, startRs, false, {
                onErrorAccept: moveToParent,
                onClose: moveToParent
            });
        }

        return handleMessage(startRs, parentForm.session);
    }
    return APIActionStatus.passed;
};
